import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import MetaMaskUtils, { IMetaMaskUtils } from '../utils/MetaMaskUtils';
import addressOnNetwork from '../constants/addressOnNetwork';
import chainTokenOnNetwork from '../constants/chainTokenOnNetwork';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { resetToInitial } from '../redux/reducers/project.slice';
import { logout, loginRequested, setToken } from '../redux/reducers/auth.slice';
import AuthAPIService from '../services/auth.service';
import Web3 from 'web3';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const authAPIService = new AuthAPIService();

type MetaMaskProviderProps = {
  children: ReactNode;
};

type MetaMaskType = {
  provider: any;
  account: string | null;
  chainId: number | null;
  chainToken: string | null;
  isConnected: boolean;
  isMetaMaskInstalled: boolean;
  addressNFT1155: string | null;
};

type contextValueType = {
  metaMask: MetaMaskType;
  loginWithMetaMask(): Promise<void>;
  disconnect(): Promise<void>;
};

const MetaMaskContext = createContext<null | contextValueType>(null);
const { Provider } = MetaMaskContext;
function useMetaMask() {
  const context = useContext(MetaMaskContext);
  if (context === null) {
    throw new Error('Please wrap your app with <MetaMaskProvider />');
  }
  return context;
}

function MetaMaskProvider({ children }: MetaMaskProviderProps) {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);

  const [metaMask, setMetaMask] = useState<MetaMaskType>({
    provider: null,
    account: null,
    chainId: null,
    chainToken: null,
    isConnected: false,
    isMetaMaskInstalled: false,
    addressNFT1155: null,
  });
  const [metaMaskUtils, setMetaMaskUtils] = useState<IMetaMaskUtils | any>(null);

  async function metaMaskInitializer() {
    const newMetaMaskUtils = new MetaMaskUtils();
    const account = await newMetaMaskUtils.requestAccount();
    const isConnected = newMetaMaskUtils.isConnected();
    const isMetaMaskInstalled = newMetaMaskUtils.isMetaMaskInstalled();
    const chainId = await newMetaMaskUtils.getChainId();
    const provider = newMetaMaskUtils.getProvider();
    const addressNFT1155 = (chainId && addressOnNetwork[chainId]) || null;
    const chainToken = (chainId && chainTokenOnNetwork[chainId]) || null;

    setMetaMaskUtils(newMetaMaskUtils);

    if (!addressNFT1155) {
      toast.error('This chain is not supported');
      return;
    }

    const accessToken = cookies.get('access_token');

    if (!accessToken) {
      setMetaMask((prev) => ({ ...prev, provider, isConnected: false, isMetaMaskInstalled, chainId, addressNFT1155 }));
      return;
    }

    dispatch(setToken({ token: accessToken }));

    setMetaMask((prev) => ({
      ...prev,
      account: account || null,
      provider,
      isConnected,
      isMetaMaskInstalled,
      chainId,
      chainToken,
      addressNFT1155,
    }));
  }

  useEffect(() => {
    metaMaskInitializer();
  }, []);

  useEffect(() => {
    if (!metaMaskUtils || !metaMaskUtils.isMetaMaskInstalled()) return;

    const provider = metaMaskUtils.getProvider();

    provider.on('accountsChanged', (accounts: string[]) => {
      const [account] = accounts;
      setMetaMask((prev) => ({ ...prev, account }));
    });

    provider.on('chainChanged', (chainId: string) => {
      window.location.reload();
    });
  }, [metaMaskUtils]);

  useEffect(() => {
    (async () => {
      if (!metaMaskUtils) return;

      const account = await metaMaskUtils.requestAccount();
      const isConnected = metaMaskUtils.isConnected();
      const newMetaMaskUtils = new MetaMaskUtils();
      const isMetaMaskInstalled = newMetaMaskUtils.isMetaMaskInstalled();
      const chainId = await newMetaMaskUtils.getChainId();
      const provider = newMetaMaskUtils.getProvider();
      const addressNFT1155 = (chainId && addressOnNetwork[chainId]) || null;
      const chainToken = (chainId && chainTokenOnNetwork[chainId]) || null;

      if (!addressNFT1155) {
        toast.error('This chain is not supported');
        return;
      }

      if (!token) return;

      window.gtag('event', 'wallet_connected', {
        address: account,
      });

      setMetaMask((prev) => ({
        ...prev,
        account,
        chainId,
        chainToken,
        isConnected,
        addressNFT1155,
        isMetaMaskInstalled,
        provider,
      }));
    })();
  }, [token]);

  async function loginWithMetaMask() {
    if (!metaMaskUtils) return;

    const account = await metaMaskUtils.requestAccount();
    const newMetaMaskUtils = new MetaMaskUtils();
    const chainId = await newMetaMaskUtils.getChainId();
    const provider = newMetaMaskUtils.getProvider();
    const addressNFT1155 = (chainId && addressOnNetwork[chainId]) || null;
    
    if (!addressNFT1155) {
      toast.error('This chain is not supported');
      return;
    }

    const toastId = toast.loading('Connecting to MetaMask');

    try {
      const data = await authAPIService.getNonce(account, chainId || 0);

      const signature = await handleSignMessage(account, data.nonce, provider);

      dispatch(loginRequested({ walletAddress: account, chainId: chainId || 0, signature: signature }));
    } catch (error) {
      console.log('error', error);
    }

    toast.dismiss(toastId);
  }

  async function disconnect() {
    console.log('remove cookie');
    cookies.remove('access_token');
    console.log('logout');
    if (!metaMaskUtils) return;

    dispatch(logout());
    dispatch(resetToInitial());

    setMetaMask({
      ...metaMask,
      provider: null,
      account: null,
      chainId: null,
      chainToken: null,
      isConnected: false,
      isMetaMaskInstalled: false,
      addressNFT1155: null,
    });
  }

  async function handleSignMessage(publicAddress: string, nonce: number, provider: any): Promise<string> {
    const web3 = new Web3(provider);

    const message = `Welcome to Storylines\n\nClick "Sign" to sign in. No password needed!\nThis request will not trigger a blockchain transaction or cost any gas fees.\n\nYour authentication will be reset after 24 hours.\n\nI accept the Storylines Terms of Service:\nhttps://storylines.video/tos\n\nWallet address: ${publicAddress}\n\nNonce:\n${nonce}`;

    return new Promise((resolve, reject) =>
      web3.eth.personal.sign(web3.utils.fromUtf8(`${message}`), publicAddress, '', (err, signature) => {
        if (err) return reject(err);
        console.log('signature', signature);
        return resolve(signature);
      }),
    );
  }

  return (
    <Provider
      value={{
        metaMask,
        loginWithMetaMask,
        disconnect,
      }}
    >
      {children}
    </Provider>
  );
}

export { MetaMaskContext, MetaMaskProvider, useMetaMask };
